// Tiny API helper for the portal. Fetches the same /api/v1/leagues that the
// dashboard widget uses, so the portal stays in sync with which sports
// actually have live data without a separate registry.

export interface League {
  slug: string
  displayName: string
  category: string
  hasData: boolean
}

export const API_BASE = 'https://grid.altsportsdata.com'

/**
 * Resolve a catalog logo path to a fetchable URL. Relative paths
 * (`/sportsbooks/x.png`, `/leagues/y.svg`) are static tiles served by
 * widget-assets behind API_BASE; absolute https URLs are Supabase Storage
 * uploads (migration 0066) and pass through untouched.
 */
export function assetUrl(path: string): string
export function assetUrl(path: string | null | undefined): string | undefined
export function assetUrl(path: string | null | undefined): string | undefined {
  if (!path)
    return undefined
  return /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`
}

// Portal calls with ?includeEmpty=true so the sidebar can surface every
// configured sport, marking ones without live odds with a "NO ODDS" pill.
export async function fetchLeagues(): Promise<League[]> {
  const res = await fetch(`${API_BASE}/api/v1/leagues?includeEmpty=true`)
  if (!res.ok)
    throw new Error(`Leagues fetch failed: ${res.status}`)
  return res.json()
}

// Full embeddable catalog — every Supabase sports row with a slug, not just
// the configured odds leagues. `configured` = an odds config exists;
// `hasData` = live odds right now. Catalog-only rows (configured: false)
// still get Distribution / Approvals / Availability Map embeds.
export interface CatalogSport {
  // Catalog PK — null only for rows unioned from configs that have no
  // catalog entry. Used to join sport-scoped relations client-side.
  id: number | null
  slug: string
  name: string
  category: string | null
  logo_url: string | null
  configured: boolean
  hasData: boolean
}

export async function fetchCatalog(): Promise<CatalogSport[]> {
  const res = await fetch(`${API_BASE}/api/v1/sports-catalog`)
  if (!res.ok)
    throw new Error(`Catalog fetch failed: ${res.status}`)
  return res.json()
}

// Map verbose API category names to the short uppercase form the brutalist
// sidebar uses. Keyed off the actual `category` strings from
// api/configs/*.json (verified 2026-04-30: only four distinct values).
// Anything unknown falls through as the raw category uppercased.
const CATEGORY_LABEL: Record<string, string> = {
  'Motor Sports': 'MOTORSPORT',
  'Combat Sports': 'COMBAT',
  'Action Sports': 'ACTION SPORTS',
  'Alternative Sports': 'ALT SPORTS',
}

export function categoryLabel(c: string | null): string {
  if (!c)
    return 'OTHER'
  return CATEGORY_LABEL[c] ?? c.toUpperCase()
}

// Sidebar category order, shared by every grouped sidebar (embed,
// distribution, affiliate, configs). Per Cole 2026-08-03: Motorsport,
// Combat, Alternative, Action.
export const GROUP_ORDER = ['MOTORSPORT', 'COMBAT', 'ALT SPORTS', 'ACTION SPORTS'] as const

export interface SportCarrier {
  sportsbook_id: number
  sportsbook_name: string | null
  brand_color: string | null
  logo_url: string | null
  logo_fill: boolean
  division_id: number | null
  division_name: string | null
  affiliate_url: string | null
}

export interface SportDetails {
  id: number
  name: string
  slug: string
  approved_states: string[]
  international: string[]
  carriers: SportCarrier[]
}

// `aff` = sub-affiliate flag: the server swaps every carrier's affiliate_url
// to that sub's own book-issued links (missing link → null → tile unlinked),
// so the Distribution preview shows exactly what the flagged embed renders.
export async function fetchSportDetails(slug: string, aff?: string): Promise<SportDetails | null> {
  const query = aff ? `?aff=${encodeURIComponent(aff)}` : ''
  const res = await fetch(`${API_BASE}/api/v1/sports/${encodeURIComponent(slug)}/details${query}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`Sport details fetch failed: ${res.status}`)
  return res.json()
}

// ── Sportsbook-keyed embeds ───────────────────────────────────────────

/**
 * Book name → URL slug, the same rule /distribution/sportsbooks/<slug> and
 * /affiliate/operators/<slug> use (and api/sportsbookEmbeds.ts resolves).
 * Books have no slug column; renaming a book changes its slug.
 */
export function sportsbookSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

// Every book with something to embed (carries >= 1 sport or has any
// availability data) — GET /api/v1/sportsbooks-catalog.
export interface CatalogSportsbook {
  id: number
  slug: string
  name: string
  kind: 'sportsbook' | 'dfs' | 'prediction'
  brand_color: string | null
  logo_url: string | null
  logo_fill: boolean
  sports_count: number
  states_count: number
  international_count: number
  has_blocklist: boolean
}

export async function fetchSportsbookCatalog(): Promise<CatalogSportsbook[]> {
  const res = await fetch(`${API_BASE}/api/v1/sportsbooks-catalog`)
  if (!res.ok)
    throw new Error(`Sportsbook catalog fetch failed: ${res.status}`)
  return res.json()
}
